import React, { useState, useEffect } from 'react';
import { App, Table, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { formatDate } from '../utils/format';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface Product {
  id: string;
  title: string;
  status: string;
  thumbnail?: string;
  type?: { id: string; value: string };
  variants?: Array<{ id: string; title: string }>;
  created_at: string;
}

const Products: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadProducts();
  }, [page, pageSize]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/products', {
        params: {
          offset: (page - 1) * pageSize,
          limit: pageSize,
        },
      });
      setProducts(response.data.products || []);
      setTotal(response.data.count || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('products.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = debouncedSearch
    ? products.filter((p) => p.title?.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : products;

  const columns = [
    {
      title: t('products.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'published' ? 'green' : 'default'}>
          {status === 'published' ? t('products.published') : t('products.draft')}
        </Tag>
      ),
    },
    {
      title: t('products.type'),
      key: 'type',
      render: (_: any, record: Product) => record.type?.value || '-',
    },
    {
      title: t('products.variants'),
      key: 'variants',
      render: (_: any, record: Product) => record.variants?.length || 0,
    },

    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('products.title')}
        </h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/products/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default Products;
